import { useMemo } from 'react';
import { PhoneAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip, InfoTipPopover, toHHmmss } from '@frontend/analytics-shared';
import { Chart } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useFetchAggregatedCallsAndDurationReport } from '../../hooks/use-fetch-aggregated-calls-and-duration-report';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { formatCallVolumeAndDurationCharts } from '../../utils/format-total-call-volume-and-duration-charts';
import { ChartProps } from './shared-types';

const colors = {
  inbound: theme.colors.secondary.seaweed30,
  outbound: theme.colors.warning50,
  total: theme.colors.primary20,
};

const labels = {
  inbound: i18next.t('Incoming', { ns: 'analytics-phone' }),
  outbound: i18next.t('Outgoing', { ns: 'analytics-phone' }),
  total: i18next.t('Total', { ns: 'analytics-phone' }),
};

export const TotalCallDurationChart = ({ defaultChartAppearance = {}, exportPdfProps, isSmallMode }: ChartProps) => {
  const { t } = useTranslation('analytics-phone');
  const { accessibleLocationsNames, filterHintText, isDemoAccount } = usePhoneAnalyticsShallowStore(
    'accessibleLocationsNames',
    'filterHintText',
    'isDemoAccount'
  );
  const { data, isHourlyData, isLoading, isMultiLocation } =
    useFetchAggregatedCallsAndDurationReport<PhoneAnalyticsTypes.CallsAggregationKey>('call_direction');

  const processedData = useMemo(
    () =>
      formatCallVolumeAndDurationCharts({
        data,
        dataKey: 'duration',
        isHourlyData,
        isMultiLocation,
        locations: accessibleLocationsNames,
      }),
    [accessibleLocationsNames, data, isHourlyData, isMultiLocation]
  );

  return (
    <Chart colors={colors} isLoading={isLoading} labels={labels}>
      <Chart.Header
        {...exportPdfProps}
        infoTip={
          <InfoTipPopover>{t('Total duration of incoming and outgoing calls. Value is in hh:mm:ss')}</InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
        subtitle={!isSmallMode && filterHintText}
        title={t('Total Call Duration')}
      />
      <Chart.BarChart
        appearance={defaultChartAppearance}
        data={processedData.chartData}
        formatValue={(value) => toHHmmss(value)}
      />
      <Chart.Legends formatValue={(value) => toHHmmss(value)} values={processedData.totals} />
    </Chart>
  );
};
